/**
 * 이상한 방 - 구획 진행판 (바꾸기 60초 동안 남들이 어디까지 왔는가, 순수 DOM)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(바꾸기 1분) · 5절(웃김 설계 포인트 - 옆 사람이 뭘 하는지가 보인다)
 *            `이상한방-상태모델-v0.1.md` 11.2절(클라이언트가 가진 것) · 11.3절(서버가 쥐는 것)
 *            로드맵 M11(각자 방 - 전원이 자기 구획을 고치고 남의 구획을 찾는다)
 *
 * ## 이 화면은 '기다림'이 아니라 '진행'이다 (M11에서 뜻이 바뀌었다)
 *
 * M4~M10에서 이 자리는 `ui/changerWait.ts`("OO이 손대는 중")였다. 한 사람이 고치고 나머지가 기다리는 구조라
 * 화면이 답할 질문이 "언제 끝나나" 하나였고, 그래서 이름 하나에 점 한 줄이었다. M11에서 **전원이 동시에 자기 구획을
 * 고친다** - 기다리는 사람이 없어지는 것이 그 마일스톤의 목적이므로 이 화면도 기다림을 말하면 안 된다. 지금 답하는
 * 질문은 **"남들은 어디까지 왔나"**이고, 그래서 줄이 사람 수만큼 선다. 파일 이름이 바뀐 이유가 이것이다.
 *
 * ## 아는 것은 여전히 셋뿐이다 - 이름 · 만든 개수 · 상한
 *
 * 남의 작업대는 뷰에 자리가 없다(`matchView.deskFor` - `desk`는 언제나 내 것이다). 남에 대해 오는 것은
 * `PublicPlayer.changeUsed`(개수)와 `changeDone`(다 했는가)뿐이고, 무엇을 건드렸는지는 **모양 자체에 필드가 없다**
 * (`ZoneProgressRow`). 필드가 없으면 그릴 수도 없고, 그것이 곧 "남의 초안은 새지 않는다"의 증명이다
 * (`zoneProgress.test.ts`가 뷰 모양과 DOM 본문에서 검사한다). 구획 이름도 싣지 않는다 - 누가 어느 구획인지는
 * 뷰(`ClientView.zones`)에 있지만 그것을 바꾸기 중에 나란히 읽히게 두면 "저 사람 구획부터 뒤지자"가 화면의 조언이 된다.
 *
 * ## 점은 내 띠(`changerBar.ts`)와 같은 문법이다
 *
 * 채운 것은 노랑, 빈 것은 옅은 테두리, 왼쪽부터 차오른다. 내 화면에서 하나 켜지는 순간 남들 화면의 내 줄에서도 같은
 * 자리에 같은 점이 켜진다 - 같은 사건을 두 화면이 같은 모양으로 말해야 "저 사람이 지금 하나 만들었다"가 곁눈으로 읽힌다.
 * 글자로 세지 않는 이유도 같다. 방을 외우는 눈을 떼지 않아야 한다(이 60초는 외우기의 연장이다).
 *
 * ## 자리는 여기서 말하지 않는다
 *
 * 좌표 규칙과 그 근거는 `zoneProgress.css` 머리말 한 곳에만 적는다 - 두 파일이 배치를 각자 말하면 한쪽이 조용히
 * 거짓말을 시작한다(실제로 그랬다). 누르는 것이 없다는 사실도 그쪽에 있다.
 *
 * ## 프레임마다 불려도 싸야 한다
 *
 * 세션이 HUD·참가자 줄과 같은 자리(`render`)에서 뷰를 옮겨 부른다(`matchSessionView.buildZoneProgressView`).
 * 바꾸기가 아니면 `null`이 오고 그때는 `hidden`이다 - 페이즈 판단은 여기 없다. 줄은 좌석이 바뀔 때만,
 * 점은 상한이 바뀔 때만 다시 만들고 나머지는 클래스 토글이다(`playerStrip.ts`와 같은 규약).
 */

import { t } from '../i18n'

import { createElement, setAttr, setFlag, setHidden, setText } from './dom'
import { overlayLayout } from './overlayLayout'

import './zoneProgress.css'

/* --- 타입 ---------------------------------------------------------------------- */

/**
 * 한 사람의 진행. 대상 id · 동사 · 자리 · 구획 이름은 **필드가 없다**(머리말).
 * 값은 호스트의 뷰에서 옮겨 온 것이다(`matchSessionView.buildZoneProgressView` - 산술 0).
 */
export type ZoneProgressRow = Readonly<{
  /** 좌석 id. 줄을 다시 만들지 말지를 가르는 데만 쓴다 - 화면에 나오지 않는다. */
  id: string
  name: string
  /** 지금까지 만든 개수(`PublicPlayer.changeUsed`). */
  used: number
  /** 이번 라운드의 인당 상한(`DeskView.budget`). 전원이 같은 값이다(`matchDesk.freshDesks`). */
  budget: number
  /** [다 했다]를 눌렀거나 이웃이다(`PublicPlayer.changeDone` - 리듀서의 마감 판정 그대로). */
  done: boolean
}>

export type ZoneProgressView = Readonly<{ rows: readonly ZoneProgressRow[] }>

export type ZoneProgressHandle = Readonly<{
  root: HTMLElement
  /** `null`이면 접는다(바꾸기가 아니거나 볼 사람이 없다). */
  render: (view: ZoneProgressView | null) => void
  /** 배치 모드·잠든 무대에서는 통째로 접는다(HUD의 `setVisible`과 같은 시점). */
  setVisible: (visible: boolean) => void
  dispose: () => void
}>

export type ZoneProgressOptions = Readonly<{ mount: HTMLElement }>

/* --- 내부 --------------------------------------------------------------------- */

type RowNodes = Readonly<{ root: HTMLElement; name: HTMLElement; dots: HTMLElement; pips: HTMLElement[] }>

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 진행판을 만들어 `mount`에 붙인다. 처음에는 **접힌 상태**다 - 첫 뷰가 오기 전에는 보일 것이 없고,
 * 라운드의 대부분(외우기·수색·리빌)에서 `null`을 받으므로 기본이 접힘이어야 첫 프레임에 빈 판이 스치지 않는다.
 */
export function createZoneProgress(options: ZoneProgressOptions): ZoneProgressHandle {
  const root = createElement('section', 'zp-root')
  root.setAttribute('role', 'status')
  root.hidden = true

  const title = createElement('p', 'zp-title', '')
  const list = createElement('ul', 'zp-list')
  const hint = createElement('p', 'zp-hint', '')
  root.append(title, list, hint)
  // 참가자 줄 바로 아래 칸. 거리를 픽셀로 적지 않는다(`overlayLayout.ts`).
  const layout = overlayLayout(options.mount)
  layout.place(root, 'zones')

  const nodes = new Map<string, RowNodes>()

  /** 바깥의 `setVisible`. `render(null)`과 별개의 스위치다 - 잠든 무대에서 바꾸기 뷰가 와도 펴지지 않는다. */
  let visible = true
  let shown = false

  function rebuild(rows: readonly ZoneProgressRow[]): void {
    nodes.clear()
    list.replaceChildren()
    for (const row of rows) {
      const item = createElement('li', 'zp-row')
      const name = createElement('span', 'zp-row__name', '')
      const dots = createElement('span', 'zp-row__dots')
      dots.setAttribute('aria-hidden', 'true')
      item.append(name, dots)
      list.append(item)
      nodes.set(row.id, { root: item, name, dots, pips: [] })
    }
  }

  function paintDots(node: RowNodes, row: ZoneProgressRow): void {
    if (node.pips.length !== row.budget) {
      node.pips.length = 0
      node.dots.replaceChildren()
      for (let index = 0; index < row.budget; index += 1) {
        const pip = createElement('i', 'zp-dot')
        node.pips.push(pip)
        node.dots.append(pip)
      }
    }
    node.pips.forEach((pip, index) => setFlag(pip, 'is-on', index < row.used))
  }

  function paintRow(row: ZoneProgressRow): void {
    const node = nodes.get(row.id)
    if (node === undefined) return
    setText(node.name, row.name)
    setFlag(node.root, 'is-done', row.done)
    // 다 한 사람은 개수를 말하지 않는다 - 남은 것이 없다는 사실이 그 사람에 대해 알 것의 전부다.
    setAttr(
      node.root,
      'aria-label',
      row.done
        ? t('zone.progress.doneAria', { name: row.name })
        : t('zone.progress.rowAria', { name: row.name, used: row.used, budget: row.budget }),
    )
    paintDots(node, row)
  }

  function sync(): void {
    setHidden(root, !(visible && shown))
  }

  return {
    root,

    render(view: ZoneProgressView | null): void {
      shown = view !== null && view.rows.length > 0
      if (view !== null) {
        setText(title, t('zone.progress.title'))
        setText(hint, t('zone.progress.hint'))
        setAttr(root, 'aria-label', t('zone.progress.aria'))
        // 좌석이 바뀔 때만 줄을 다시 만든다(프레임마다 불리는 경로다). 그 뒤로는 값만 다시 칠한다.
        const ids = view.rows.map((row) => row.id)
        if (ids.length !== nodes.size || ids.some((id) => !nodes.has(id))) rebuild(view.rows)
        for (const row of view.rows) paintRow(row)
      }
      sync()
      // 줄 수가 바뀌면 왼쪽 열이 잘리는지도 바뀐다. 관측기는 배경 탭에서 오지 않으므로 여기서 직접 알린다.
      layout.syncClip()
    },

    setVisible(next: boolean): void {
      visible = next
      sync()
      layout.syncClip()
    },

    dispose(): void {
      // 기둥에서 걷는다 - `remove()`만 하면 관측기가 떼어 낸 노드를 계속 붙든다.
      layout.unplace(root)
    },
  }
}
